import { getAuthInfoFromLocalStorage, clearSession, storeSession } from "../context/AuthContext";
import { gotoOAuthPortal, refreshAuthInfoAsync } from "./auth";

// One refresh at a time: several requests can 401 together, and each starting
// its own refresh would race and burn the token.
let refreshing: Promise<void> | null = null;

async function refreshOnce() {
  if (!refreshing) {
    refreshing = (async () => {
      const info = getAuthInfoFromLocalStorage();
      if (!info) throw new Error("no session to refresh");
      storeSession(await refreshAuthInfoAsync(info));
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
}

function send(path: string, options: RequestInit, idToken: string) {
  return fetch(`${process.env.REACT_APP_API_SERVER}${path}`, {
    ...options,
    headers: { ...options["headers"], Authorization: `Bearer ${idToken}` },
  });
}

/**
 * wrapper of browser's native fetch method
 */
async function request(path: string, options: RequestInit) {
  const authInfo = getAuthInfoFromLocalStorage();
  if (!authInfo?.id_token) {
    gotoOAuthPortal("login")
    throw new Error("Access Denied. (Reason: no auth token is available)")
  }

  let response = await send(path, options, authInfo.id_token);

  // Id and access tokens last a day, the refresh token 30. A 401 usually means
  // the day is up, not that the session is over -- so renew and retry once
  // before making the user sign in again.
  if (response.status === 401) {
    try {
      await refreshOnce();
      const renewed = getAuthInfoFromLocalStorage();
      if (!renewed?.id_token) throw new Error("refresh produced no token");
      response = await send(path, options, renewed.id_token);
    } catch (err) {
      console.warn("[auth] refresh failed, signing out", err);
      clearSession()
      gotoOAuthPortal("login")
      throw new Error("Token expired. (code: 401)")
    }
  }

  if (response.ok) {
    const data = await response.json()
    return data;
  }

  if (response.status === 401) {
    // still 401 after a successful refresh: not a token problem
    clearSession()
    gotoOAuthPortal("login")
    throw new Error("Token expired.")
  }
  const resText = await response.text();
  if (resText && resText.trim()) {
    throw new Error(resText.trim())
  } else {
    throw new Error(response.statusText)
  }
}

export { request }
